import { useState, useRef, useEffect, useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { IconButton } from "@atlaskit/button-next/new";
import { NewCoreIconProps } from "@atlaskit/icon";
import AiGenerativeTextSummaryIcon from "@atlaskit/icon/core/ai-generative-text-summary";
import CopyIcon from "@atlaskit/icon/core/copy";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import LinkIcon from "@atlaskit/icon/core/link";
import ThumbsDownIcon from "@atlaskit/icon/core/thumbs-down";
import ThumbsUpIcon from "@atlaskit/icon/core/thumbs-up";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { Tracking } from "@/analytics/atl-analytics/tracking";
import { Link, Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import { Source } from "./ai-summary";
import { useAISummaryContext } from "./ai-summary-context";
import { MarkdownRenderer } from "./renderer/markdown-renderer";

const sourceWrapperStyle = css({
    "display": "inline-flex",
    "justifyContent": "center",
    "alignItems": "center",
    "maxWidth": "44px",
    "height": token("space.250"),
    "borderRadius": token("space.050"),
    "backgroundColor": wacToken("color.extended.blue100"),
    "padding": token("space.050"),

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > div": {
        "display": "flex",
        "align-items": "center",
        "justify-content": "center",
        "height": "100%",
        "width": "100%"
    }
});

const sourceIconWrapperStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    gap: token("space.025"),
    color: wacToken("color.extended.blue500"),
    cursor: "pointer"
});

const linkIconStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center"
});

const sourceTextStyle = css({
    display: "inline-flex",
    alignItems: "center",
    font: token("font.body"),
    lineHeight: 1
});

const SourceIcon = ({
    identifier,
    sources
}: {
    identifier: number;
    sources?: Source[];
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const sourceTitle = sources?.[identifier - 1]?.title;
    const sourceAriaLabel = sourceTitle || `Source ${identifier}`;

    const handleClick = () => {
        const sourceIndex = identifier - 1; // Convert 1-based citation to 0-based array index
        if (sources && sources[sourceIndex]?.url) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "link",
                actionSubjectId: "searchAiSummaryInlineSourceLink",
                source: "searchAiSummary",
                attributes: {
                    sourceIndex: sourceIndex,
                    sourceTitle: sources[sourceIndex].title,
                    sourceUrl: sources[sourceIndex].url
                }
            }).fire();
            // This functionality needs to be updated to filter out relevant sources, instead of directly linking to the source page.
            window.open(
                sources[sourceIndex].url,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    return (
        <div css={sourceWrapperStyle}>
            <Tooltip content={sourceTitle}>
                <div
                    css={sourceIconWrapperStyle}
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            handleClick();
                        }
                    }}
                    aria-label={sourceAriaLabel}
                >
                    <LinkIcon
                        size="medium"
                        label=""
                        color={token("color.icon.brand")}
                        css={linkIconStyle}
                    />
                    {identifier > 1 && (
                        <span css={sourceTextStyle}>· {identifier}</span>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};

const StatusComponent = (props: { text?: string; sources?: Source[] }) => (
    <SourceIcon
        identifier={parseInt(props.text || "1", 10)}
        sources={props.sources}
    />
);

const fullContent = css({
    maxHeight: "fit-content",
    paddingBlockEnd: token("space.300")
});

const defaultContent = css({
    maxHeight: "249px",
    overflow: "hidden",
    position: "relative"
});

const blurOverlayStyle = css({
    background: `linear-gradient(180deg, transparent 0%, ${token(
        "elevation.surface.overlay",
        "#FFFFFF"
    )} 50%)`,
    width: "100%",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    insetBlockEnd: 0,
    paddingBlockEnd: "0",
    paddingBlockStart: token("space.800"),
    color: token("color.text", "#172B4D")
});

const showMoreButtonStyle = css({
    "display": "inline-flex",
    "justifyContent": "center",
    "alignItems": "center",
    "height": `${token("space.500")}`,
    "width": "100%",
    "maxWidth": "none", // overrides max-width set on Button component
    "border": `1px solid ${token(
        "color.background.accent.gray.subtle.hovered"
    )}`,
    "borderRadius": `${token("space.500")}`,
    "cursor": "pointer",
    "fontWeight": 400,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span": {
        fontWeight: 400
    },

    "&:hover": {
        textDecoration: "none !important"
    }
});

const aiSummaryHeaderStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.050"),
    marginBlockEnd: token("space.150")
});

const aiSummaryIconWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: token("space.300"),
    height: token("space.300"),
    borderRadius: token("space.050"),
    backgroundColor: token("color.background.accent.blue.subtlest")
});

const aiSummaryFooterStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "padding": token("space.050"),

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span": {
        lineHeight: "21px"
    }
});

const aiSummaryFooterSectionStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    gap: token("space.050")
});

const aiSummaryFooterButtonSectionStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span": {
        lineHeight: "17px"
    }
});

const disclaimerTextStyle = css({
    color: token("color.text.subtlest"),
    fontSize: "14px"
});

const FeedbackThumpsUpIcon = (
    iconProps: NewCoreIconProps,
    feedbackState: "thumbsUp" | "thumbsDown" | null
) => {
    return (
        <ThumbsUpIcon
            {...iconProps}
            color={
                feedbackState === "thumbsUp"
                    ? token("color.icon.selected")
                    : undefined
            }
        />
    );
};

const FeedbackThumpsDownIcon = (
    iconProps: NewCoreIconProps,
    feedbackState: "thumbsUp" | "thumbsDown" | null
) => {
    return (
        <ThumbsDownIcon
            {...iconProps}
            color={
                feedbackState === "thumbsDown"
                    ? token("color.icon.selected")
                    : undefined
            }
        />
    );
};

export const AISummaryFooter = () => {
    const intl = useIntl();
    const href = useLocalizedHref("/trust/atlassian-intelligence");
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { summary, likeFeedback, dislikeFeedback } = useAISummaryContext();
    const aiDisclaimerText = intl.formatMessage({
        id: "ai.summary.answer.footer.disclaimer",
        defaultMessage: "Uses AI. Verify results."
    });

    const handleCopy = async () => {
        try {
            // Remove citation markers like [1], [2], etc. from the text
            const textToCopy = summary.replace(/\[\d+\]/g, "").trim();
            await navigator.clipboard.writeText(textToCopy);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const thumbsUpText = intl.formatMessage({
        id: "ai.summary.answer.footer.thumbsUp",
        defaultMessage: "Good response"
    });
    const thumbsDownText = intl.formatMessage({
        id: "ai.summary.answer.footer.thumbsDown",
        defaultMessage: "Bad response"
    });
    const copyText = intl.formatMessage({
        id: "ai.summary.answer.footer.copy",
        defaultMessage: "Copy"
    });

    const [feedbackState, setFeedbackState] = useState<
        "thumbsUp" | "thumbsDown" | null
    >(null);

    const handleFeedbackClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        state: "thumbsUp" | "thumbsDown"
    ) => {
        event.preventDefault();
        setFeedbackState(state);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "feedbackButton",
            actionSubjectId: "searchAiSummaryFeedbackClicked",
            source: "searchAiSummary",
            attributes: {
                feedback: state
            }
        }).fire();

        if (state === "thumbsUp") {
            likeFeedback();
        } else {
            dislikeFeedback();
        }
    };

    useEffect(() => {
        Tracking.autoTrackLinks(true);
    }, []);

    return (
        <div css={aiSummaryFooterStyle}>
            <div css={aiSummaryFooterSectionStyle}>
                <InformationCircleIcon
                    color={token("color.icon.subtlest")}
                    size="medium"
                    label=""
                />
                <Link
                    text={aiDisclaimerText}
                    type="anchor"
                    href={href}
                    aria-label={aiDisclaimerText}
                    css={disclaimerTextStyle}
                    anchorProps={{ target: "_blank" }}
                    dataEventProps={{
                        "data-product-key": "search",
                        "data-event": "clicked",
                        "data-event-component": "linkButton",
                        "data-event-container": "searchAiSummary",
                        "data-label-english": "Uses AI"
                    }}
                />
            </div>
            <div
                css={[
                    aiSummaryFooterSectionStyle,
                    aiSummaryFooterButtonSectionStyle
                ]}
            >
                <IconButton
                    appearance="subtle"
                    icon={(iconProps) =>
                        FeedbackThumpsUpIcon(iconProps, feedbackState)
                    }
                    label={thumbsUpText}
                    onClick={(event) => handleFeedbackClick(event, "thumbsUp")}
                    isTooltipDisabled={false}
                />
                <IconButton
                    appearance="subtle"
                    icon={(iconProps) =>
                        FeedbackThumpsDownIcon(iconProps, feedbackState)
                    }
                    label={thumbsDownText}
                    onClick={(event) =>
                        handleFeedbackClick(event, "thumbsDown")
                    }
                    isTooltipDisabled={false}
                />
                <IconButton
                    appearance="subtle"
                    icon={CopyIcon}
                    label={copyText}
                    onClick={handleCopy}
                    isTooltipDisabled={false}
                />
            </div>
        </div>
    );
};

const markdownContainerStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& div": {
        fontSize: `${token("font.body")} !important`
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& a": {
        marginInlineStart: "0 !important"
    }
});

export const AISummaryAnswer = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { summary, sources } = useAISummaryContext();

    const statusComponent = useCallback(
        (props: { text?: string; sources?: Source[] }) => (
            <StatusComponent {...props} sources={sources} />
        ),
        [sources]
    );

    const renderMarkdownContent = (text: string) => {
        // Use the search-ai approach: encodeAdf + Atlaskit renderer
        // This handles both markdown formatting and citations properly
        return (
            <div css={markdownContainerStyle}>
                <MarkdownRenderer
                    answerString={text}
                    statusComponent={statusComponent}
                />
            </div>
        );
    };

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            if (contentRef.current) {
                const wrapperHeight = contentRef.current.offsetHeight;
                const contentHeight = contentRef.current.scrollHeight;
                setIsTruncated(contentHeight > wrapperHeight);
            }
        });

        resizeObserver.observe(contentRef.current);
        return () => resizeObserver.disconnect();
    }, [summary]);

    const handleToggleExpansion = () => {
        setIsExpanded(!isExpanded);
        createAnalyticsEvent({
            eventName: isExpanded
                ? "searchAiSummaryReadLessClicked"
                : "searchAiSummaryReadMoreClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: isExpanded
                ? "searchAiSummaryReadLess"
                : "searchAiSummaryReadMore",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                labelEnglish: isExpanded ? "Read less" : "Read more"
            }
        }).fire();
    };

    const showMoreText = intl.formatMessage({
        id: "ai.summary.answer.showMoreText",
        defaultMessage: "Show more"
    });

    const aiOverviewText = intl.formatMessage({
        id: "ai.summary.answer.overview",
        defaultMessage: "AI overview"
    });

    return (
        <div>
            <div css={aiSummaryHeaderStyle}>
                <div css={aiSummaryIconWrapperStyle}>
                    <AiGenerativeTextSummaryIcon
                        size="medium"
                        label={aiOverviewText}
                        color={token("color.icon.brand")}
                    />
                </div>
                {aiOverviewText}
            </div>
            <div
                ref={contentRef}
                css={[defaultContent, isExpanded && fullContent]}
            >
                {renderMarkdownContent(summary)}
                {isTruncated && !isExpanded && (
                    <div css={blurOverlayStyle}>
                        <Button
                            variant="secondary"
                            css={showMoreButtonStyle}
                            onClick={handleToggleExpansion}
                            aria-label={showMoreText}
                        >
                            {showMoreText}
                        </Button>
                    </div>
                )}
            </div>
            {isExpanded && <AISummaryFooter />}
        </div>
    );
};
